import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { rewardsApi } from '../lib/api';
import { Trophy, Star, Shield, Flame, Zap, Crown, Medal, Flag, MessageSquare, BookOpen, Camera, Users, Gift, ArrowRight, ChevronRight, Coins, ShoppingBag, History, Award } from 'lucide-react';

const ICON_MAP = {
  'message-square': MessageSquare, 'book-open': BookOpen, 'megaphone': Users,
  'camera': Camera, 'star': Star, 'crown': Crown, 'flag': Flag,
  'trophy': Trophy, 'medal': Medal, 'flame': Flame, 'zap': Zap,
  'shield': Shield, 'swords': Award, 'message-circle': MessageSquare,
};

const TIER_COLORS = {
  Bronze: { bg: '#CD7F32', text: '#FFF', border: '#CD7F32' },
  Silver: { bg: '#C0C0C0', text: '#1a1a1a', border: '#C0C0C0' },
  Gold: { bg: '#FFD700', text: '#1a1a1a', border: '#FFD700' },
  Ninja: { bg: '#E63946', text: '#FFF', border: '#E63946' },
};

const POINT_ACTIONS_INFO = [
  { action: 'Post to community', points: 10, icon: MessageSquare },
  { action: 'Post with photo', points: 20, icon: Camera },
  { action: 'Log challenge progress', points: 15, icon: Flame },
  { action: 'Complete a challenge', points: 100, icon: Trophy },
  { action: 'Leave a comment', points: 5, icon: MessageSquare },
  { action: 'Get a like on your post', points: 2, icon: Star },
  { action: 'Follow someone', points: 3, icon: Users },
  { action: 'Daily login', points: 5, icon: Zap },
];

function BadgeCard({ badge, earned, small }) {
  const Icon = ICON_MAP[badge.icon] || Award;
  return (
    <div className={`border transition-all ${earned ? 'bg-zinc-900/60 border-zinc-700' : 'bg-zinc-900/20 border-zinc-800/40 opacity-50'} ${small ? 'p-3' : 'p-4'}`}
      data-testid={`badge-${badge.badge_id}`}>
      <div className="flex items-center gap-3">
        <div className={`${small ? 'w-8 h-8' : 'w-10 h-10'} flex items-center justify-center border ${earned ? 'border-[#FFD700]/40 text-[#FFD700]' : 'border-zinc-800 text-zinc-600'}`}>
          <Icon className={small ? 'w-4 h-4' : 'w-5 h-5'} />
        </div>
        <div className="flex-1 min-w-0">
          <span className={`block ${small ? 'text-xs' : 'text-sm'} ${earned ? 'text-white' : 'text-zinc-500'}`} style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            {badge.name}
          </span>
          {!small && <p className="text-zinc-500 text-[10px] mt-0.5">{badge.description}</p>}
          {earned && badge.points_bonus > 0 && (
            <span className="text-[#00F0FF] text-[10px]">+{badge.points_bonus} pts bonus</span>
          )}
        </div>
        {earned && (
          <div className="flex-shrink-0 w-5 h-5 bg-[#FFD700]/20 flex items-center justify-center">
            <Star className="w-3 h-3 text-[#FFD700] fill-[#FFD700]" />
          </div>
        )}
      </div>
    </div>
  );
}

function TierCard({ tier, isCurrent, isLocked }) {
  const colors = TIER_COLORS[tier.name] || TIER_COLORS.Bronze;
  return (
    // eslint-disable-next-line no-nested-ternary -- readable tier picker
    <div className={`border p-5 transition-all ${isCurrent ? 'border-2' : isLocked ? 'opacity-50 border-zinc-800' : 'border-zinc-800'}`}
      style={isCurrent ? { borderColor: colors.border } : {}}
      data-testid={`tier-${tier.name.toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 flex items-center justify-center" style={{ background: isCurrent ? colors.bg : 'transparent', border: `1px solid ${isLocked ? '#333' : colors.border}` }}>
          {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
          <Shield className="w-5 h-5" style={{ color: isCurrent ? colors.text : isLocked ? '#555' : colors.bg }} />
        </div>
        <div>
          // eslint-disable-next-line no-nested-ternary -- readable tier picker
          <span className="text-sm font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif', color: isCurrent ? colors.bg : isLocked ? '#555' : colors.bg }}>
            {tier.name}
          </span>
          <span className="text-zinc-600 text-[10px]">{tier.min_points}+ pts</span>
        </div>
        {isCurrent && (
          <span className="ml-auto px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold" style={{ background: colors.bg, color: colors.text }}>
            Current
          </span>
        )}
      </div>
      <ul className="space-y-1">
        {tier.perks.map((perk) => (
          <li key={perk} className={`text-xs flex items-center gap-2 ${isCurrent ? 'text-zinc-300' : 'text-zinc-500'}`}>
            <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: isLocked ? '#555' : colors.bg }} /> {perk}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RedeemSection({ rewards, token, onRedeemed }) {
  const [amount, setAmount] = useState('500');
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const dollarValue = Math.round(Number(amount || 0) / 100 * 100) / 100;
  const canRedeem = rewards.available_points >= Number(amount) && Number(amount) >= 100;

  async function handleRedeem() {
    if (!canRedeem) return;
    setRedeeming(true);
    setError('');
    setResult(null);
    try {
      const res = await rewardsApi.redeem(Number(amount), token);
      setResult(res.data);
      onRedeemed();
    } catch (e) {
      setError(e.response?.data?.detail || 'Redemption failed');
    }
    setRedeeming(false);
  }

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 p-5" data-testid="redeem-section">
      <h3 className="text-white text-sm uppercase tracking-wider mb-4 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
        <Gift className="w-4 h-4 text-[#00F0FF]" /> Redeem Points
      </h3>
      <p className="text-zinc-400 text-xs mb-4">Convert your points into store credit. 100 points = $1.00</p>

      <div className="space-y-3">
        <div>
          <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Points to Redeem</label>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} min="100" step="100"
            className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 focus:outline-none focus:border-zinc-500"
            data-testid="redeem-amount-input" />
        </div>
        <div className="flex items-center justify-between p-3 bg-zinc-800/40 border border-zinc-700">
          <span className="text-zinc-400 text-xs">Store Credit Value</span>
          <span className="text-[#00F0FF] text-lg font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>${dollarValue.toFixed(2)}</span>
        </div>
        <button onClick={handleRedeem} disabled={!canRedeem || redeeming}
          className="w-full py-2.5 bg-[#00F0FF] text-black text-xs uppercase tracking-wider font-bold disabled:opacity-40 hover:bg-[#00F0FF]/80 transition-colors"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="redeem-submit-btn"
        >{redeeming ? 'Redeeming...' : 'Redeem for Store Credit'}</button>
        {error && <p className="text-[#E63946] text-xs">{error}</p>}
        {result && (
          <div className="p-3 bg-green-900/30 border border-green-700/50" data-testid="redeem-result">
            <p className="text-green-400 text-xs mb-1">Redeemed! Your discount code:</p>
            <p className="text-white text-lg font-bold font-mono tracking-wider" data-testid="redeem-code">{result.code}</p>
            <p className="text-zinc-400 text-[10px] mt-1">Use this code at checkout for ${result.dollar_value} off</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RewardsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rewards, setRewards] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rewardsRes, badgesRes, historyRes, lbRes] = await Promise.all([
        rewardsApi.getMyRewards(token),
        rewardsApi.getBadges(),
        rewardsApi.getHistory(token),
        rewardsApi.getLeaderboard(),
      ]);
      setRewards(rewardsRes.data);
      setAllBadges(badgesRes.data.badges || []);
      setHistory(historyRes.data.history || []);
      setLeaderboard(lbRes.data.leaderboard || []);

      // Record daily login
      rewardsApi.dailyLogin(token).catch(() => {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (!user || !token) { navigate('/auth'); return; }
    loadData();
  }, [user, token, navigate, loadData]);

  if (loading || !rewards) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing"><div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-zinc-900/50 border border-zinc-800" />)}</div></div>
      </main>
    );
  }

  const tierColors = TIER_COLORS[rewards.tier] || TIER_COLORS.Bronze;
  const earnedBadgeIds = new Set(rewards.badges || []);
  const nextTier = rewards.next_tier;
  const progressToNext = nextTier ? Math.min(100, Math.round(((rewards.total_points - (rewards.current_tier?.min_points || 0)) / (nextTier.min_points - (rewards.current_tier?.min_points || 0))) * 100)) : 100;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'badges', label: `Badges (${rewards.badge_count || 0})` },
    { id: 'earn', label: 'How to Earn' },
    { id: 'history', label: 'History' },
    { id: 'leaderboard', label: 'Leaderboard' },
  ];

  return (
    <main className="pt-20 min-h-screen" data-testid="rewards-page">
      <div className="container-ninja section-spacing">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <Link to="/community" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-zinc-300 transition-colors mb-3 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Community</Link>
            <h1 className="text-white text-4xl sm:text-5xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="rewards-title">Rewards</h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-md">Earn points, unlock badges, and redeem for free merch and discounts.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center px-4 py-3 border" style={{ borderColor: tierColors.bg }}>
              <span className="text-2xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif', color: tierColors.bg }} data-testid="total-points">{rewards.total_points}</span>
              <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Total Points</span>
            </div>
            <div className="text-center px-4 py-3 bg-zinc-900/60 border border-zinc-800">
              <span className="text-2xl font-bold block text-[#00F0FF]" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="available-points">{rewards.available_points}</span>
              <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Available</span>
            </div>
            <div className="text-center px-3 py-2">
              <Shield className="w-6 h-6 mx-auto mb-1" style={{ color: tierColors.bg }} />
              <span className="text-xs font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', color: tierColors.bg }} data-testid="current-tier">{rewards.tier}</span>
            </div>
          </div>
        </div>

        {/* Tier progress bar */}
        {nextTier && (
          <div className="mb-8 p-4 bg-zinc-900/40 border border-zinc-800" data-testid="tier-progress">
            <div className="flex justify-between text-xs uppercase tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              <span style={{ color: tierColors.bg }}>{rewards.tier}</span>
              <span className="text-zinc-400">{rewards.points_to_next_tier} pts to {nextTier.name}</span>
              <span className="text-zinc-500">{nextTier.name}</span>
            </div>
            <div className="h-2 bg-zinc-800 overflow-hidden">
              <div className="h-full transition-all duration-700" style={{ width: `${progressToNext}%`, background: tierColors.bg }} />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-zinc-800 overflow-x-auto" data-testid="rewards-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-5 py-3 text-xs uppercase tracking-wider transition-colors whitespace-nowrap ${activeTab === t.id ? 'text-[#E63946] border-b-2 border-[#E63946]' : 'text-zinc-500 hover:text-zinc-300'}`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid={`tab-${t.id}`}
            >{t.label}</button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Tiers */}
              <div>
                <h2 className="text-white text-sm uppercase tracking-wider mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Reward Tiers</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(rewards.current_tier ? [
                    { name: 'Bronze', min_points: 0, discount_pct: 0, perks: ['Community access'] },
                    { name: 'Silver', min_points: 500, discount_pct: 5, perks: ['5% member discount', 'Silver badge'] },
                    { name: 'Gold', min_points: 2000, discount_pct: 10, perks: ['10% discount', 'Free shipping', 'Gold badge'] },
                    { name: 'Ninja', min_points: 5000, discount_pct: 15, perks: ['15% discount', 'Free shipping', 'Free merch item', 'Ninja badge'] },
                  ] : []).map(tier => (
                    <TierCard key={tier.name} tier={tier} isCurrent={tier.name === rewards.tier} isLocked={tier.min_points > rewards.total_points} />
                  ))}
                </div>
              </div>

              {/* Recent badges */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white text-sm uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Recent Badges</h2>
                  <button onClick={() => setActiveTab('badges')} className="text-[#00F0FF] text-xs hover:underline">View All</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(rewards.badges_detail || []).slice(0, 4).map(b => (
                    <BadgeCard key={b.badge_id} badge={b} earned small />
                  ))}
                  {(rewards.badges_detail || []).length === 0 && (
                    <p className="text-zinc-500 text-sm col-span-2">Complete actions to earn badges!</p>
                  )}
                </div>
              </div>
            </div>

            <aside className="space-y-6">
              <RedeemSection rewards={rewards} token={token} onRedeemed={loadData} />

              {/* Quick earn actions */}
              <div className="bg-zinc-900/40 border border-zinc-800 p-5">
                <h3 className="text-white text-sm uppercase tracking-wider mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Quick Earn</h3>
                <div className="space-y-2">
                  <Link to="/community" className="flex items-center gap-3 p-2 hover:bg-zinc-800/40 transition-colors">
                    <MessageSquare className="w-4 h-4 text-zinc-500" />
                    <span className="text-zinc-300 text-xs flex-1">Share a post</span>
                    <span className="text-[#00F0FF] text-xs font-bold">+10 pts</span>
                  </Link>
                  <Link to="/community/challenges" className="flex items-center gap-3 p-2 hover:bg-zinc-800/40 transition-colors">
                    <Trophy className="w-4 h-4 text-zinc-500" />
                    <span className="text-zinc-300 text-xs flex-1">Join a challenge</span>
                    <span className="text-[#00F0FF] text-xs font-bold">+15 pts/log</span>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        )}

        {activeTab === 'badges' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {allBadges.map(b => (
              <BadgeCard key={b.badge_id} badge={b} earned={earnedBadgeIds.has(b.badge_id)} />
            ))}
          </div>
        )}

        {activeTab === 'earn' && (
          <div className="max-w-2xl">
            <h2 className="text-white text-lg uppercase tracking-wider mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>How to Earn Points</h2>
            <div className="space-y-2">
              {POINT_ACTIONS_INFO.map((a, i) => (
                <div key={a.action} className="flex items-center gap-4 p-4 bg-zinc-900/40 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <a.icon className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                  <span className="text-zinc-300 text-sm flex-1">{a.action}</span>
                  <span className="text-[#00F0FF] text-sm font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>+{a.points} pts</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-zinc-900/40 border border-[#00F0FF]/30">
              <h3 className="text-[#00F0FF] text-sm mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Points to Dollars</h3>
              <p className="text-zinc-400 text-sm">100 points = $1.00 store credit. Redeem any time from the Rewards page. Codes work at checkout!</p>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="max-w-2xl space-y-1">
            {history.length > 0 ? history.map((h, i) => (
              <div key={`${h.created_at}-${h.reason}-${h.points}`} className="flex items-center gap-4 p-3 bg-zinc-900/40 border border-zinc-800" data-testid={`history-${i}`}>
                <span className={`text-sm font-bold w-16 text-right ${h.points > 0 ? 'text-[#00F0FF]' : 'text-[#E63946]'}`} style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {h.points > 0 ? '+' : ''}{h.points}
                </span>
                <span className="text-zinc-400 text-xs flex-1">{h.description || h.action}</span>
                <span className="text-zinc-600 text-[10px] flex-shrink-0">
                  {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            )) : (
              <p className="text-zinc-500 text-sm text-center py-12">No point history yet. Start earning!</p>
            )}
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="max-w-2xl space-y-1">
            {leaderboard.map((u, i) => {
              const tc = TIER_COLORS[u.tier] || TIER_COLORS.Bronze;
              return (
                <div key={u.username || u.user_id} className="flex items-center gap-4 p-3 bg-zinc-900/40 border border-zinc-800" data-testid={`points-lb-${i}`}>
                  <span className="text-sm font-bold w-8 text-center text-zinc-500" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{i + 1}</span>
                  <Link to={`/community/${u.username}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-xs font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : u.display_name?.charAt(0) || '?'}
                    </div>
                    <span className="text-white text-sm truncate hover:text-[#00F0FF] transition-colors">{u.display_name || u.username}</span>
                  </Link>
                  <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold" style={{ color: tc.bg, border: `1px solid ${tc.bg}30` }}>{u.tier}</span>
                  <span className="text-white text-sm font-bold w-20 text-right" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{u.total_points} pts</span>
                </div>
              );
            })}
            {leaderboard.length === 0 && (
              <p className="text-zinc-500 text-sm text-center py-12">No one on the leaderboard yet!</p>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
